import { useRef, useEffect, useState, useCallback } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Volume2, VolumeX, Loader2 } from "lucide-react";

/**
 * LiveVideo — cinematic, self-managing video surface.
 * • Poster-first; the <video> is only given a src when it nears the viewport (IO).
 * • Autoplays MUTED once ~40% visible; pauses when scrolled away (saves CPU).
 * • Desktop: pointer enter → unmute (respecting autoplay policy); leave → mute.
 * • Touch: tap toggles audio; playback keeps running.
 * • Never bypasses browser autoplay security; if the browser refuses sound, the clip keeps playing muted and offers Tap for sound.
 * • Respects prefers-reduced-motion (no autoplay; poster + manual tap to play).
 */
export function LiveVideo({ item, className = "", rounded = "rounded-2xl", showMeta = true, priority = false, testid }) {
  const prm = useReducedMotion();
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const audioIntentRef = useRef(false);
  const [near, setNear] = useState(priority);   // src attached
  const [playing, setPlaying] = useState(false);
  const [audible, setAudible] = useState(false);
  const [ready, setReady] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);   // browser refused sound: offer a tap
  const isTouch = typeof window !== "undefined" && window.matchMedia?.("(hover: none)").matches;

  // attach src + autoplay when near viewport; pause when away
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setNear(true);
          const v = vidRef.current;
          if (v && !prm) v.play?.().then(() => setPlaying(true)).catch(() => setPlaying(false));
        } else {
          const v = vidRef.current;
          if (v) {
            audioIntentRef.current = false;
            v.pause?.();
            setPlaying(false);
            setAudible(false);
            setNeedsTap(false);
            v.muted = true;
          }
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [prm]);

  const enableAudio = useCallback(async (on) => {
    // Keep the pointer's intent even when lazy-loading has not mounted the
    // <video> yet. The video is attached on intersection; the effect below
    // applies this intent as soon as that element exists.
    audioIntentRef.current = on;
    const v = vidRef.current;
    if (!v) return;
    if (!on) {
      v.muted = true;
      setAudible(false);
      setNeedsTap(false);
      return;
    }

    // Sound needs a tap on the page first: browser autoplay policy does not
    // count a hover as a tap. Until the viewer has tapped, keep the clip
    // playing muted and offer a "Tap for sound" button, whose click is the
    // gesture the browser needs. No userActivation API means no check.
    const tapped = navigator.userActivation ? navigator.userActivation.hasBeenActive : true;
    if (!tapped) {
      v.muted = true;
      if (v.paused) v.play?.()?.catch?.(() => {});
      setAudible(false);
      setNeedsTap(true);
      return;
    }

    // Start a not-yet-playing clip muted first. Calling play() after
    // unmuting can be rejected by browser autoplay policy; the old code
    // then left the hovered sample frozen on its poster/last frame.
    if (v.paused) {
      v.muted = true;
      try {
        await v.play?.();
        setPlaying(true);
      } catch (_) {
        setAudible(false);
        return;
      }
      // The pointer may have left (or the clip may have scrolled away)
      // while play() was pending. Honor the latest intent, never unmute late.
      if (!audioIntentRef.current) return;
    }

    v.muted = false;
    setAudible(true);
    setNeedsTap(false);
  }, []);

  useEffect(() => {
    if (!near || isTouch || prm || !audioIntentRef.current) return;
    enableAudio(true);
  }, [near, isTouch, prm, enableAudio]);

  const onEnter = () => { if (!isTouch && !prm) enableAudio(true); };
  const onLeave = () => { if (!isTouch) enableAudio(false); };
  const onTap = () => {
    const v = vidRef.current;
    if (!v) return;
    if (prm && v.paused) { v.play?.().then(() => setPlaying(true)).catch(() => {}); return; }
    enableAudio(!audible);
  };

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={isTouch ? onTap : undefined}
      className={`group relative overflow-hidden ${rounded} bg-[#0a0b13] ${className}`}
    >
      {/* poster underlay — always present, fades once video paints */}
      <img
        src={item.poster} alt={item.title}
        loading={priority ? "eager" : "lazy"} decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
        style={{ opacity: playing && ready ? 0 : 1, transition: "opacity .6s ease" }}
      />
      {near && (
        <video
          ref={vidRef}
          src={item.src}
          poster={item.poster}
          muted loop playsInline preload="metadata"
          autoPlay={!prm}
          onCanPlay={() => setReady(true)}
          onPlaying={() => setPlaying(true)}
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* cinematic frame: hairline key light + vignette (glass language) */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-0 left-[10%] right-[10%] h-px bg-[linear-gradient(90deg,transparent,rgba(124,112,255,0.55),transparent)] opacity-70" />
        <div className="absolute inset-0 bg-[radial-gradient(130%_100%_at_50%_0%,transparent_55%,rgba(6,7,13,0.5)_100%)]" />
      </div>

      {/* loading shimmer before first frame */}
      {near && !ready && !prm && (
        <div className="absolute inset-0 grid place-items-center">
          <Loader2 className="w-6 h-6 text-white/50 animate-spin" />
        </div>
      )}

      {/* audio state chip (desktop) / tap hint (touch) */}
      <div className="absolute top-3 right-3 flex items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
           style={{ opacity: (audible || isTouch) ? 1 : undefined }}>
        <span className="grid place-items-center w-8 h-8 rounded-full bg-black/55 border border-white/15 backdrop-blur text-white">
          {audible ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
        </span>
      </div>

      {/* browsers refuse sound until the viewer taps the page: offer that tap */}
      {needsTap && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); enableAudio(true); }}
          className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 rounded-full bg-black/65 border border-white/20 px-3 py-1.5 text-[11px] font-mono uppercase tracking-[0.14em] text-white backdrop-blur"
        >
          <Volume2 className="w-3.5 h-3.5" />
          Tap for sound
        </button>
      )}

      {/* elegant metadata — rises on hover, never a giant play button */}
      {showMeta && (
        <motion.div
          initial={false}
          className="absolute inset-x-0 bottom-0 p-4 sm:p-5 pointer-events-none"
        >
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/25 to-transparent" />
          <div className="relative translate-y-1.5 group-hover:translate-y-0 transition-transform duration-500">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-[#c9c1ff] px-2 py-0.5 rounded-md bg-black/50 border border-white/10 backdrop-blur">{item.tag}</span>
              <span className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45">{item.label}</span>
            </div>
            <div className="font-display text-lg sm:text-xl font-semibold text-white mt-2" style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}>{item.title}</div>
            <div className="text-[12px] sm:text-sm text-slate-300/90 mt-0.5 max-w-md opacity-0 group-hover:opacity-100 transition-opacity duration-500 leading-snug">{item.blurb}</div>
          </div>
        </motion.div>
      )}
    </div>
  );
}
